import { OVERWERK_GRENS, periodeTotaal, postenRows, roosterRows, urenRows } from '../lib/periode';
import { useGeld } from '../lib/geld';
import { useQuery } from '@tanstack/react-query';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { CalendarRange, Clock, Coins, Wallet } from 'lucide-react';
import { api, type CalendarPeriod } from '../lib/api';
import { cn, formatDate, formatHours, formatMoney } from '../lib/utils';
import { Kpi } from './ui';

/** Overwerkgrens per 4-weken periode (CAO). */

/**
 * Kerncijfers boven de kalender: de loonperiode van de getoonde datum, de uitbetaling,
 * uren t.o.v. de 152u-overwerkgrens en het bruto tot nu toe.
 */
export function PeriodBar({ period }: { period: CalendarPeriod | undefined }) {
  const { modus, label } = useGeld();
  const { data: summary } = useQuery({
    queryKey: ['period-summary', period?.year, period?.period_number],
    queryFn: () => api.shifts.periodSummary(period!.year, period!.period_number),
    enabled: !!period,
  });
  if (!period) return null;

  const hours = summary ? summary.uren_voor_grens ?? summary.worked_hours ?? 0 : null;
  const daysToPay = period.payday ? differenceInCalendarDays(parseISO(period.payday), new Date()) : null;

  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
      <Kpi
        icon={CalendarRange}
        label="Loonperiode"
        text={`P${period.period_number}`}
        rows={[
          ['Van', formatDate(period.start_date)],
          ['Tot en met', formatDate(period.end_date)],
          ...(hours != null ? roosterRows(summary?.rooster, hours, formatHours, (d) => formatDate(d, 'dd-MM')) : []),
        ]}
      />
      <Kpi
        icon={Wallet}
        label="Uitbetaling"
        text={
          daysToPay == null
            ? '–'
            : daysToPay === 0
              ? 'Vandaag'
              : daysToPay > 0
                ? `${daysToPay} ${daysToPay === 1 ? 'dag' : 'dagen'}`
                : formatDate(period.payday!, 'dd-MM')
        }
        rows={period.payday ? [[daysToPay != null && daysToPay < 0 ? 'Betaald op' : 'Betaaldag', formatDate(period.payday)]] : undefined}
      />
      <Kpi
        icon={Clock}
        label="Uren periode"
        value={hours}
        format={formatHours}
        rows={hours != null ? urenRows(summary?.soorten, hours, formatHours) : undefined}
        bar={hours != null ? { value: hours, max: OVERWERK_GRENS, over: hours > OVERWERK_GRENS } : undefined}
      />
      <Kpi
        icon={Coins}
        label={`${label} periode`}
        value={periodeTotaal(summary?.totaal, modus, summary?.reiskosten)}
        format={formatMoney}
        tone="money"
        rows={summary ? postenRows(summary.posten, modus, summary.reiskosten) : undefined}
      />
    </div>
  );
}
